// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, TooltipProps, Typography } from '@mui/material';

interface IOverflowTooltipTextProps {
  text: string;
  sx: object;
  // Dense lists point the tooltip sideways, add a dwell delay, and push it
  // clear of trailing row controls so it never buries neighbouring rows
  // (AUT-6409 tile rows). popperOffset is popper's [skidding, distance].
  placement?: TooltipProps['placement'];
  enterDelay?: number;
  popperOffset?: [number, number];
}

// Single-line text that ellipsizes when it outgrows its box, and then - and only then -
// offers the full value in a hover tooltip. Text that fits gets no tooltip at all (an empty
// title disables MUI's Tooltip). Truncation is measured on the rendered node and re-measured
// when the box resizes, so the answer tracks the responsive card widths.
export const OverflowTooltipText = ({
  text, sx, placement, enterDelay, popperOffset,
}: IOverflowTooltipTextProps) => {
  const textRef = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;
    // Return the previous value when it is unchanged so React bails out of the re-render, and never
    // write a value that is the same as the one already shown. (AUT-6757)
    const measure = () => setIsTruncated((prev) => {
      const next = node.scrollWidth > node.clientWidth;
      return next === prev ? prev : next;
    });
    measure();
    // jsdom has no ResizeObserver; there, the initial measure (0 > 0, never truncated) stands.
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Coalesce observer callbacks into one measurement per animation frame rather than measuring
    // synchronously inside the callback: reading scrollWidth/clientWidth forces layout, and doing it
    // inline is the shape that can re-enter the observer in the same frame and raise Chrome's
    // "ResizeObserver loop" notice. One rAF measurement per frame plus the compare above means this
    // observer cannot originate that loop.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; measure(); });
    });
    observer.observe(node);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [text]);

  return (
    <Tooltip
      title={isTruncated ? text : ''}
      arrow
      placement={placement}
      enterDelay={enterDelay}
      enterNextDelay={enterDelay}
      PopperProps={popperOffset && {
        modifiers: [{ name: 'offset', options: { offset: popperOffset } }],
      }}
      componentsProps={{
        tooltip: {
          // Filenames and machine names have no spaces; without this the
          // tooltip only wraps at hyphens and turns into odd shapes.
          sx: { maxWidth: 240, overflowWrap: 'anywhere' },
        },
      }}
    >
      <Typography ref={textRef} noWrap sx={sx}>
        {text}
      </Typography>
    </Tooltip>
  );
};

OverflowTooltipText.defaultProps = {
  placement: 'top',
  enterDelay: 100,
  popperOffset: undefined,
};

export default OverflowTooltipText;
